<template>
  <FriedModal v-if="showImporter" blur-background :closable="false">
    <template #header>
      <FriedModalHeader :header="$t('create_document_import_title', { name: document.name })" />
    </template>
    <template v-if="uploadError" #footer>
      <FriedModalButtonsFooter>
        <FriedButton data-external="create-document-upload-error-button">
          {{ $t('reload-document') }}
        </FriedButton>
      </FriedModalButtonsFooter>
    </template>

    <Importer
      :payload="importPayload"
      :progress-stalled-text="$t('import-progress-stalled-reload-document')"
      :progress-stalled-button-text="$t('reload-document')"
      :document-pusher-channel="newDocumentPusherChannel"
      @pages-imported="redirect"
      @cancel="redirect"
      @progress-stalled-clicked="redirect"
      @error="importFailed"
    />
  </FriedModal>
  <FriedModal
    v-else
    size="large"
    :loading="createDocumentLoading"
    class="create-document-modal-container"
    :blur-background="showOverlayLoader"
    :closable="!showOverlayLoader"
    @close="handleClose"
  >
    <template #container>
      <div v-if="showOverlayLoader" class="loading-overlay">
        <FriedSpinner />
      </div>
      <TransitionFadeInOut mode="out-in">
        <CreateDocumentForm
          v-if="visibleSection === 'form'"
          class="create-document-modal-container"
          :document="document"
          :selected-template="selectedTemplateDetails"
          :has-qes-files="hasQesFiles"
          :disable-create="disableCreate"
          :loading="showOverlayLoader"
          :header="formHeaderText"
          @update="formData = { ...formData, ...$event }"
          @close="emit('close')"
          @back="handleBack"
          @create="createDocument"
        />
        <CreateDocumentContentLibrary
          v-else-if="visibleSection === 'content-library'"
          class="create-document-modal-container"
          :path="path"
          :directories="directories"
          :fetching="fetchingMore"
          @select-folder="path = $event"
          @select="selectTemplate"
          @back="handleBackContentLibrary"
        />
        <FriedModalSubContainer v-else class="create-document-modal-container">
          <template #header>
            <FriedModalHeader :header="initialHeaderText"> </FriedModalHeader>
          </template>
          <FriedMessage
            v-if="!!templateDetailsError"
            type="error"
            :title="t('something-went-wrong')"
          >
            <FriedParagraph>
              {{ $t('please-try-again-or-contact') }}
              <FriedLink href="https://help.getaccept.com/" target="_blank">
                {{ $t('Support') }}
              </FriedLink>
            </FriedParagraph>
          </FriedMessage>
          <div class="create-document-modal-wrapper">
            <nav
              v-if="isXSmallScreenAndUp"
              v-show="visibleSection === 'template' || !hasUploadedFiles"
              class="tab-container"
            >
              <template v-if="!!lastUsedCount">
                <FriedTab
                  v-for="tab in tabs"
                  :key="tab.key"
                  v-model="visibleSection"
                  :value="tab.key"
                  type="primary"
                >
                  {{ tab.label }}
                </FriedTab>
              </template>
              <FriedButtonSplit
                v-if="showGoogleDocs"
                class="start-from-scratch-button"
                data-external="create-document-modal-start-from-scratch-button"
                button-type="secondary"
                @click="handleClickStartFromScratch"
              >
                <template #leftIcon>
                  <FriedIcon icon="plus-large" />
                </template>
                {{ t('start-off-fresh') }}
                <template #dropdownItems>
                  <FriedDropdownItem
                    data-external="create-document-modal-create-from-drive-button"
                    @click="initializeFilePicker"
                  >
                    <template #leftIcon>
                      <FriedIcon icon="google-drive" />
                    </template>
                    {{ t('create_modal_create_from_google_docs') }}
                  </FriedDropdownItem>
                </template>
              </FriedButtonSplit>
              <FriedButton
                v-else
                class="start-from-scratch-button"
                data-external="create-document-modal-start-from-scratch-button"
                button-type="tertiary"
                @click="handleClickStartFromScratch"
              >
                <template #leftIcon>
                  <FriedIcon icon="plus-large" />
                </template>
                {{ t('start-off-fresh') }}
              </FriedButton>
            </nav>
            <TransitionFadeInOut mode="out-in">
              <section v-if="visibleSection === 'template'" class="create-document-modal-content">
                <FriedH5>{{ t('recently-used') }}</FriedH5>
                <div v-if="isXSmallScreenAndUp" class="last-used-container">
                  <CreateDocumentLastUsedCard
                    v-for="template in lastUsedTemplates"
                    :key="template.id"
                    :label="template.title"
                    :data-external="`create-document-last-used-template-button:${template.title}`"
                    :content-id="template.contentId"
                    @click="selectTemplate(template)"
                  />
                </div>
                <div v-else class="last-used-container">
                  <CreateDocumentItemCard
                    v-for="template in lastUsedTemplates"
                    :key="template.id"
                    :label="template.title"
                    :data-external="`create-document-last-used-template-button:${template.title}`"
                    @click="selectTemplate(template)"
                  />
                </div>
                <div v-click-outside="handleSearchBlur" class="search-wrapper">
                  <FriedSearchInput
                    v-model="searchInput"
                    :placeholder="t('search-for-templates')"
                    :class="['search-input', { focus: isFocused }]"
                    @focus="handleSearchFocus"
                  >
                    <template #left>
                      <FriedIcon icon="search" />
                    </template>
                  </FriedSearchInput>
                  <FriedButton
                    data-external="create-document-modal-browse-folder-button"
                    button-type="tertiary"
                    @click="visibleSection = 'content-library'"
                  >
                    {{ t('cr_select_template_from_library') }}
                  </FriedButton>
                </div>
                <CreateDocumentTemplateTable
                  :search="searchInputDebounced"
                  :overflow="isXSmallScreenAndDown ? 'visible' : 'auto'"
                  :enabled="enabled"
                  @select="selectTemplate"
                />
              </section>
              <CreateDocumentUpload
                v-else-if="visibleSection === 'upload'"
                class="create-document-modal-content"
                :document-id="documentId"
                :uploaded-files="files"
                :qes-files="qesFiles"
                @update-files="handleUpdateFiles"
                @reload-qes-files="loadQesFiles"
              />
            </TransitionFadeInOut>
          </div>
          <template v-if="visibleSection === 'upload' && hasUploadedFiles" #footer>
            <FriedModalButtonsFooter>
              <template v-if="isXSmallScreenAndUp" #left>
                <FriedButton
                  data-external="create-document-upload-back-button"
                  button-type="tertiary"
                  :disabled="showOverlayLoader"
                  @click="visibleSection = 'template'"
                >
                  <template #leftIcon>
                    <FriedIcon icon="arrow-left" />
                  </template>
                  {{ t('back') }}
                </FriedButton>
              </template>
              <FriedButton
                data-external="create-document-upload-close-button"
                button-type="secondary"
                @click="handleClose"
              >
                {{ t('cancel') }}
              </FriedButton>
              <FriedButton
                data-external="create-document-upload-continue-button"
                form="create-communication-template-form"
                button-type="primary"
                :disabled="disableContinue"
                @click="handleContinueUpload"
              >
                {{ t('continue') }}
              </FriedButton>
            </FriedModalButtonsFooter>
          </template>
        </FriedModalSubContainer>
      </TransitionFadeInOut>
    </template>
  </FriedModal>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import type { ComputedRef } from 'vue';
import { ref, computed, watch, onBeforeMount } from 'vue';
import useBreakpoints from '@getaccept/lib-shared/src/composables/breakpoints.composable';
import type { Resource } from '@getaccept/lib-shared/src/resources/types/resource';
import { useRouter } from 'vue-router';
import { pusherClientInternal } from '@getaccept/lib-shared/src/internal/pusher';
import { ChannelType } from '@getaccept/pusher';
import { FileHelper } from '@getaccept/lib-shared/src/file/helpers/file.helper';
import { refDebounced, useAsyncState } from '@vueuse/core';
import { usePusherChannel } from '@getaccept/lib-shared/src/pusher';
import { Feature, useFeatureStore } from '@getaccept/feature-restriction';
import { storeToRefs } from 'pinia';
import { ResourceSortAttribute } from '@getaccept/lib-shared/src/resources/types/enum/resource-sort-attribute';
import { useAsyncData } from '@getaccept/lib-shared/src/composables/use-async-data';
import { SortOrder } from '@getaccept/fried-tofu';
import type { ImportPayload } from 'src/document-manipulator/create-page/importer/types/import-payload';
import type { CreateDocumentSection } from '../types/create-document-section';
import type {
  CreateDocumentFormData,
  CreateDocumentPatch,
  CreateDocumentTemplateData,
} from '../types/create-document-types';
import { useCreateDocument } from '../composables/create-document.composable';
import { useCreateInitialDocument } from '../composables/create-initial-document.composable';
import { QesService } from '../../documents/qes/services/qes.service';
import { CreateDocumentService } from '../services/create-document.service';
import { DocumentsService } from '../../documents/services/documents.service';
import { useGoogleDrive } from '../composables/google-drive.composable';
import { ResourceService } from '../../api/resources/services/resource.service';
import { defaultResourceFilter, lastUsedMaxCount } from '../document-create.constants';
import { DirectoryService } from '../../api/directories/services/directory.service';
import Importer from '../../document-manipulator/create-page/importer/components/Importer.vue';
import CreateDocumentForm from './CreateDocumentForm.vue';
import CreateDocumentUpload from './CreateDocumentUpload.vue';
import CreateDocumentTemplateTable from './CreateDocumentTemplateTable.vue';
import CreateDocumentLastUsedCard from './CreateDocumentLastUsedCard.vue';
import CreateDocumentContentLibrary from './CreateDocumentContentLibrary.vue';
import CreateDocumentItemCard from './CreateDocumentItemCard.vue';

const { t } = useI18n();
const router = useRouter();
const { features } = storeToRefs(useFeatureStore());

const { selectedType = 'template' } = defineProps<{ selectedType?: CreateDocumentSection }>();
const visibleSection = ref<CreateDocumentSection>(selectedType || 'template');

const { state: lastUsedData, isReady: lastUsedLoaded } = useAsyncState(
  ResourceService.loadList({
    ...defaultResourceFilter,
    limit: lastUsedMaxCount,
    sortBy: ResourceSortAttribute.LastUsed,
    sortOrder: SortOrder.Desc,
  }),
  null,
  {
    onSuccess: ({ totalCount }) => {
      if (!totalCount) {
        visibleSection.value = 'upload';
      }
    },
  }
);

const lastUsedTemplates = computed(() => lastUsedData.value?.resources);
const lastUsedCount = computed(() => lastUsedData.value?.totalCount);

const {
  state: initialDocument,
  isLoading: createDocumentLoading,
  isReady: initialDocumentLoaded,
  execute: createInitialDocument,
} = useCreateInitialDocument();

const documentId = computed(() => initialDocument.value?.id);
const showOverlayLoader = ref(false);
const uploadError = ref(false);

const emit = defineEmits<{ close: [] }>();

const redirect = () => {
  router.push(`/document/edit/${documentId.value}`);
};

watch(
  () => router.currentRoute.value.fullPath,
  () => {
    if (router.currentRoute.value.fullPath.includes('document/edit')) {
      emit('close');
    }
  }
);

const { channel: newDocumentPusherChannel } = usePusherChannel(
  pusherClientInternal,
  computed(() => ({
    type: ChannelType.Document,
    documentId: documentId.value,
  })),
  {
    enabled: initialDocumentLoaded,
  }
);

const { isXSmallScreenAndUp, isXSmallScreenAndDown } = useBreakpoints();

const tabs: ComputedRef<{ key: CreateDocumentSection; label: string }[]> = computed(() => [
  { key: 'template', label: t('use-template'), icon: 'template' },
  { key: 'upload', label: t('Upload document') },
]);

const formData = ref<CreateDocumentFormData>();

const selectedTemplateDetails = ref<CreateDocumentTemplateData>();
const selectedTemplate = ref<Resource>();

const files = ref<File[]>([]);

const document: ComputedRef<CreateDocumentPatch> = computed(() => ({
  ...initialDocument.value,
  ...selectedTemplateDetails.value,
  ...formData.value,
}));

const pickedDriveFile = ref<{ fileUrl: string; fileName: string }>();

const importPayload = computed<ImportPayload>(() => {
  if (pickedDriveFile.value) {
    return {
      pageIndex: 1,
      documentId: documentId.value,
      documentType: document.value.type,
      fileUrl: pickedDriveFile.value.fileUrl,
      fileName: pickedDriveFile.value.fileName,
    };
  }

  if (files.value.length) {
    return {
      pageIndex: 1,
      documentId: documentId.value,
      documentType: document.value.type,
      files: files.value,
    };
  }
  return null;
});

const showImporter = computed(() => showOverlayLoader.value && !!importPayload.value);

const importFailed = () => {
  uploadError.value = true;
};

const { execute: loadQesFiles, state: qesFiles } = useAsyncState(
  () => QesService.getQesFiles(documentId.value),
  null,
  { immediate: false }
);

const hasQesFiles = computed(() => !!qesFiles.value?.length);
const hasUploadedFiles = computed(() => !!files.value.length || hasQesFiles.value);

const disableCreate = computed(() => !document.value.name || !document.value.type);
const disableContinue = computed(() => !files.value.length && !hasQesFiles.value);

const resetData = () => {
  files.value = [];
  qesFiles.value = [];
  formData.value = null;
  selectedTemplateDetails.value = null;
  selectedTemplate.value = null;
};

const {
  state: templateDetails,
  execute: loadTemplateDetails,
  error: templateDetailsError,
} = useAsyncState((id: string) => CreateDocumentService.getTemplate(id), null, {
  immediate: false,
});

const selectTemplate = async (templateResource: Resource) => {
  if (hasUploadedFiles.value) {
    resetData();
  }

  await loadTemplateDetails(0, templateResource.contentId);
  selectedTemplateDetails.value = { ...templateDetails.value };
  selectedTemplate.value = templateResource;
  visibleSection.value = 'form';
};

const searchInput = ref('');
const searchInputDebounced = refDebounced(searchInput, 500);

const isFocused = ref(false);
const handleSearchFocus = () => {
  isFocused.value = true;
};
const handleSearchBlur = () => {
  if (!isFocused.value) return;
  isFocused.value = false;
};

const enabled = computed(() => lastUsedLoaded.value && !!lastUsedData.value?.totalCount);

const path = ref('/');
const { result: directories, fetchingMore } = useAsyncData(DirectoryService.loadDirectories, path, {
  initData: [],
  keepPreviousResult: true,
  enabled: computed(() => enabled.value),
});

const handleBackContentLibrary = () => {
  visibleSection.value = 'template';
  path.value = '/';
};

const handleUpdateFiles = (newFiles: File[]) => {
  selectedTemplateDetails.value = null;
  selectedTemplate.value = null;
  if (!files.value.length && newFiles?.length === 1) {
    formData.value = {
      ...formData.value,
      name: FileHelper.removeExtension(newFiles[0].name),
    };
  }
  if (newFiles.length > files.value.length) {
    const uniqueFiles = newFiles.filter(file => !files.value.find(f => f.name === file.name));
    files.value = [...files.value, ...uniqueFiles];
    return;
  }
  files.value = newFiles;
};

const handleContinueUpload = () => {
  if (selectedTemplateDetails.value) {
    resetData();
  }
  visibleSection.value = 'form';
};

const handleClickStartFromScratch = () => {
  resetData();
  visibleSection.value = 'form';
};

const initialHeaderText = computed(() => {
  if (visibleSection.value === 'template' && isXSmallScreenAndDown.value) {
    return t('cr_create_document_from_template');
  }
  return visibleSection.value === 'upload' && !!lastUsedCount.value
    ? t('cr_create_document_from_upload')
    : t('cr_how_would_you_like_to_start');
});

const formHeaderText = computed(() => {
  if (hasUploadedFiles.value) {
    return t('cr_create_document_from_upload');
  }
  if (selectedTemplate.value) {
    return t('cr_create_document_from_template');
  }
  return t('cr_create_document_from_scratch');
});

const { state: isGoogleDocsConnected, isReady: googleDocsLoaded } = useAsyncState(
  CreateDocumentService.loadGoogleDocs,
  null
);

const showGoogleDocs = computed(
  () =>
    !features.value[Feature.WorkflowIntegrations].restricted &&
    isGoogleDocsConnected.value &&
    googleDocsLoaded.value
);

const { initializeFilePicker, onFilePicked } = useGoogleDrive();

onFilePicked(({ fileName, fileUrl }) => {
  pickedDriveFile.value = { fileName, fileUrl };
  formData.value = {
    ...formData.value,
    name: FileHelper.removeExtension(fileName) || fileName || '',
  };
  visibleSection.value = 'form';
});

const handleBack = () => {
  if (hasUploadedFiles.value) {
    visibleSection.value = 'upload';
    return;
  }
  if (selectedTemplate.value) {
    selectedTemplateDetails.value = null;
    selectedTemplate.value = null;
  }
  visibleSection.value = 'template';
};

const { execute: create, isReady } = useCreateDocument(documentId);

const createDocument = async () => {
  showOverlayLoader.value = true;

  await create(0, formData.value, selectedTemplate.value, !!importPayload.value);

  // if you have files uploaded, wait for the pusher event before redirecting
  if (isReady.value && !importPayload.value) {
    redirect();
  }
};

onBeforeMount(() => {
  createInitialDocument(0);
});

const { execute: deleteDocument } = useAsyncState(DocumentsService.deleteDocuments, null, {
  immediate: false,
});

const handleClose = () => {
  if (!formData.value?.name || !selectedTemplateDetails.value) {
    deleteDocument(0, [documentId.value]);
  }
  emit('close');
};
</script>

<style lang="scss" scoped>
.create-document-modal {
  &-container {
    height: 800px;
    @media only screen and (max-width: $xs), (max-height: $sm) {
      height: 95vh;
    }
  }

  &-google-drive {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-100);
    padding: var(--spacing-100) 0;
  }

  &-wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-150);
    align-items: flex-start;
    padding-top: var(--spacing-50);
    overflow: hidden;
    height: inherit;

    .tab-container {
      display: flex;
      width: 100%;

      .start-from-scratch-button {
        align-self: center;
        margin: auto 0 auto auto;
      }
    }
  }

  &-content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-100);
    height: 100%;
    width: 100%;

    @media only screen and (max-width: $xs) {
      overflow: auto;
    }

    .search-wrapper {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(275px, 1fr));
      gap: var(--spacing-200);
      margin: var(--spacing-150) 0 var(--spacing-50);

      :last-child {
        justify-self: end;
      }

      @media only screen and (max-width: $xs) {
        gap: var(--spacing-100);
        margin: var(--spacing-50);

        :last-child {
          justify-self: initial;
          grid-row-start: 1;
        }
      }
    }

    .last-used-container {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: var(--spacing-100);
      border-inline: 2px solid transparent;

      @media only screen and (max-width: $xs) {
        grid-template-columns: 1fr;
      }
    }
  }
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: var(--radius-medium);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
}
</style>
